import { useState } from 'react';
import { Button, Drawer, Group, Select, Stack, TagsInput, TextInput } from '@mantine/core';
import type { ModuleDraft } from '../../staff/catalogue';

// Sentinel option value for "no tier required" — requiresTier is null on the wire/draft,
// but Mantine's Select can't bind a real option to a null value, so this maps to/from it.
// Uppercase puts it outside the slug grammar (^[a-z0-9_-]+$ is lowercase-only), so it can never
// collide with a real tier slug the way a lowercase sentinel (e.g. "__none__") could.
const NO_TIER = '__NONE__';

interface ModuleDrawerProps {
  opened: boolean;
  item: ModuleDraft;
  tierSlugs: string[];
  onSave: (m: ModuleDraft) => void;
  onClose: () => void;
}

export function ModuleDrawer({ opened, item, tierSlugs, onSave, onClose }: Readonly<ModuleDrawerProps>) {
  // Re-seed local state whenever a different draft item is opened. Adjusted during render
  // (not in an effect) per https://react.dev/learn/you-might-not-need-an-effect — avoids an
  // extra commit and the react-hooks/set-state-in-effect lint rule.
  const [prevItem, setPrevItem] = useState(item);
  const [features, setFeatures] = useState<string[]>(item.features);
  const [requiresTier, setRequiresTier] = useState<string>(item.requiresTier ?? NO_TIER);

  if (item !== prevItem) {
    setPrevItem(item);
    setFeatures(item.features);
    setRequiresTier(item.requiresTier ?? NO_TIER);
  }

  const requiresTierOptions = [
    { value: NO_TIER, label: 'Available on every tier' },
    ...tierSlugs.map((slug) => ({ value: slug, label: slug })),
  ];

  const handleSave = () => {
    onSave({
      slug: item.slug,
      features,
      requiresTier: requiresTier === NO_TIER ? null : requiresTier,
    });
  };

  return (
    <Drawer opened={opened} onClose={onClose} title={`Edit module: ${item.slug}`} position="right" size="md">
      <Stack gap="md">
        <TextInput label="Slug" value={item.slug} disabled />
        <TagsInput
          label="Features"
          data={features}
          value={features}
          onChange={setFeatures}
          placeholder="Add a feature"
        />
        <Select
          label="Requires tier"
          data={requiresTierOptions}
          value={requiresTier}
          onChange={(v) => setRequiresTier(v ?? NO_TIER)}
          allowDeselect={false}
        />
        <Group justify="flex-end" gap={10}>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
